import React from 'react';
import SmartProductImage from './SmartProductImage';
import {
    EMBELLISHMENT_TYPES,
    getGroupPlayerName,
    getGroupPlayerNumber,
    handleMissingImage,
    handleWhiteBgDetection,
} from './pressingHelpers';
import { useT } from './i18n/I18nContext';
import { getPreviewImageUrls, shopifyResize } from './previewImage';

// Pure presentation component. No drag handling — the parent wraps this in
// useDraggable and passes `dragHandleRef` / `dragHandleProps` as needed.
//
// Props:
//   group: { gid, items, groupData }
//   pickStatus?: 'not-picked' | 'partial' | 'picked' | null
//                 — drives the pick-state badge; null = no badge (fresh order)
//   onClick?: () => void — tapped (used when in Pressing column to open detail)
//   dragAttrs, dragListeners, dragRef — forwarded from @dnd-kit/core useDraggable
//   isDragging?: boolean — styling for the ghost

export default function BundleCard({
    group, pickStatus, onClick, scanOnly, overrideActive,
    dragAttrs, dragListeners, dragRef, isDragging,
    skuNotification,  // string message, if this bundle's parent SKU has an active notification
    tpOverridesByName,  // { playerName: {override_number, order_number, ...} } — TP wins
}) {
    const t = useT();
    const parent = group.items.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || group.items[0];
    const playerName   = getGroupPlayerName(group);
    const rawPlayerNumber = getGroupPlayerNumber(group);
    // TP override wins over the order-embedded number — same rule as
    // BundleDetail. A yellow-highlighted badge tells the presser the number
    // was updated by the customer in the Team Portal after checkout.
    const tpOverride = playerName && tpOverridesByName ? tpOverridesByName[playerName] : null;
    const isTpOverride = !!(tpOverride
        && String(tpOverride.override_number || '') !== String(rawPlayerNumber || ''));
    const playerNumber = isTpOverride ? tpOverride.override_number : rawPlayerNumber;
    const garmentTitle = parent?.title || t('Garment');
    const variantTitle = parent?.variant?.title && parent.variant.title !== 'Default Title'
        ? parent.variant.title : '';

    // Prefer line-item-group preview images (custom), fall back to product image
    const allAttrsForPreview = [
        ...(group.groupData?.customAttributes || []),
        ...(parent?.customAttributes || []),
        ...((group.items || []).filter(li => li !== parent).flatMap(li => li?.customAttributes || [])),
    ];
    const { front: previewFront, back: previewBack } = getPreviewImageUrls(allAttrsForPreview);
    const fallbackImg  = parent?.image?.url;

    // Only render the slots we actually have. If neither preview exists, fall
    // back to a single product-image slot. Duplicating a single image in both
    // Front AND Back slots was pointless and misleading.
    const slots = [];
    if (previewFront) slots.push({ key: 'front', label: t('Front'),     src: shopifyResize(previewFront, 800) });
    if (previewBack)  slots.push({ key: 'back',  label: t('Back side'), src: shopifyResize(previewBack,  800) });
    if (slots.length === 0) slots.push({ key: 'single', label: '', src: null, fallback: true });
    const imgsClass = `ppb-card__imgs${slots.length === 1 ? ' ppb-card__imgs--single' : ''}`;

    return (
        <div
            ref={dragRef}
            className={`ppb-card${isDragging ? ' ppb-card--dragging' : ''}${scanOnly ? ' ppb-card--scan-only' : ''}${overrideActive ? ' ppb-card--override-active' : ''}`}
            onClick={onClick}
            {...(dragAttrs || {})}
            {...(dragListeners || {})}
        >
            <div className="ppb-card__head">
                <div className="ppb-card__name">
                    {playerName || garmentTitle}
                </div>
                {playerNumber && (
                    <div className="ppb-card__number"
                         style={isTpOverride ? {
                             background: '#fef3c7',
                             color: '#78350f',
                             padding: '2px 8px',
                             borderRadius: 6,
                             border: '1px solid #fbbf24',
                         } : undefined}
                         title={isTpOverride
                             ? `Team Portal override — order had #${rawPlayerNumber}, updated to #${playerNumber}`
                             : undefined}>
                        #{playerNumber}
                    </div>
                )}
            </div>
            <div className="ppb-card__garment">
                {garmentTitle}
                {variantTitle && <span className="ppb-card__variant">{variantTitle}</span>}
                {parent?.sku && (
                    <span className="ppb-card__sku">
                        {parent.sku}
                    </span>
                )}
                {skuNotification && (
                    <div style={{
                        display: 'inline-block',
                        marginLeft: 8,
                        padding: '5px 12px',
                        borderRadius: 14,
                        background: '#fde8e8',
                        color: '#c00',
                        fontWeight: 700,
                        fontSize: '1em',
                        border: '2px solid #fbb',
                        verticalAlign: 'middle',
                    }}>
                        {skuNotification}
                    </div>
                )}
            </div>
            <div className={imgsClass}>
                {slots.map(s => (
                    <div key={s.key} className="ppb-card__img">
                        {s.fallback
                            ? fallbackImg
                                ? <SmartProductImage src={fallbackImg} alt="" transparent />
                                : <span className="ppb-card__img-fallback">📦</span>
                            : <img
                                src={s.src}
                                alt={s.label}
                                draggable={false}
                                onLoad={handleWhiteBgDetection}
                                onError={handleMissingImage}
                              />}
                        {s.label && <span className="ppb-card__img-label">{s.label}</span>}
                    </div>
                ))}
            </div>
            {pickStatus === 'not-picked' && (
                <span className="ppb-card__warn ppb-card__warn--red">⚠ {t('Not Picked')}</span>
            )}
            {pickStatus === 'partial' && (
                <span className="ppb-card__warn ppb-card__warn--amber">⚠ {t('Partially Picked')}</span>
            )}
            {(group.items || []).some(li => li._shopifyMisreportedShipped) && (
                <span className="ppb-card__warn ppb-card__warn--amber" title={t('Embellishment was marked shipped in Shopify in an earlier wave, but the kit is incomplete — please verify and press.')}>
                    ⚠ {t('Bundle recovery — verify embellishment')}
                </span>
            )}
        </div>
    );
}
